import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';

import { UserManagementService } from '../../../core/services/user-management.service';
import { getApiErrorMessage } from '../../../core/utils/api-error.util';
import { RoleManagementApiService } from '../../role-management/services/role-management-api.service';
import { RoleDetailResponse } from '../../role-management/models/role-management-api.model';
import { PolicyApiService } from '../services/policy-api.service';
import { PolicyDetailResponse } from '../models/policy-api.model';
import { PortalUserDetail } from '../models/user-management-api';
import { UserManagementActions } from './user-management.actions';

interface UserBundle {
  user: PortalUserDetail;
  roles: Record<string, RoleDetailResponse>;
  policies: Record<string, PolicyDetailResponse>;
}

@Injectable()
export class UserManagementEffects {
  private readonly actions$ = inject(Actions);
  private readonly users = inject(UserManagementService);
  private readonly roles = inject(RoleManagementApiService);
  private readonly policies = inject(PolicyApiService);

  readonly loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserManagementActions.loadUser),
      switchMap(({ userId, includeDetails = true }) =>
        this.loadUser(userId, includeDetails).pipe(
          map((bundle) => UserManagementActions.loadUserSuccess(bundle)),
          catchError((error: unknown) =>
            of(
              UserManagementActions.loadUserFailure({
                error: getApiErrorMessage(error, 'Unable to load user details'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly loadPolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserManagementActions.loadPolicy),
      switchMap(({ policyId }) =>
        this.policies.getUserAssignedPolicy(policyId).pipe(
          map((policy) => UserManagementActions.loadPolicySuccess({ policy })),
          catchError((error: unknown) =>
            of(
              UserManagementActions.loadPolicyFailure({
                error: getApiErrorMessage(error, 'Unable to load policy details'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserManagementActions.updateUser),
      switchMap(({ userId, payload }) =>
        this.users.updateUser(userId, payload).pipe(
          switchMap(() => this.loadBundle(userId)),
          map((bundle) =>
            UserManagementActions.updateUserSuccess({
              ...bundle,
              message: 'User updated successfully',
            }),
          ),
          catchError((error: unknown) =>
            of(
              UserManagementActions.updateUserFailure({
                error: getApiErrorMessage(error, 'Unable to update user'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly setStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserManagementActions.setUserStatus),
      switchMap(({ userId, active, deactivationReason }) =>
        (active
          ? this.users.markUserActive(userId)
          : this.users.markUserInactive(userId, {
              deactivation_reason: deactivationReason ?? '',
            })
        ).pipe(
          map((response) =>
            UserManagementActions.setUserStatusSuccess({
              active,
              message: response.message,
            }),
          ),
          catchError((error: unknown) =>
            of(
              UserManagementActions.setUserStatusFailure({
                error: getApiErrorMessage(error, 'Unable to update user status'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  private loadBundle(userId: string): Observable<UserBundle> {
    return this.users.getUser(userId).pipe(
      switchMap((user) => {
        const roleRequests = Object.fromEntries(
          user.roles.map((role) => [role.id, this.roles.getUserScopedRole(role.id)]),
        );
        const policyIds = Array.from(
          new Set([...user.policy_ids, ...user.roles.flatMap((role) => role.policy_ids)]),
        );
        const policyRequests = Object.fromEntries(
          policyIds.map((policyId) => [policyId, this.policies.getUserAssignedPolicy(policyId)]),
        );
        const roles$ = Object.keys(roleRequests).length
          ? forkJoin(roleRequests)
          : of({} as Record<string, RoleDetailResponse>);
        const policies$ = Object.keys(policyRequests).length
          ? forkJoin(policyRequests)
          : of({} as Record<string, PolicyDetailResponse>);
        return forkJoin({ user: of(user), roles: roles$, policies: policies$ });
      }),
    );
  }

  private loadUser(userId: string, includeDetails: boolean): Observable<UserBundle> {
    if (includeDetails) {
      return this.loadBundle(userId);
    }

    return this.users.getUser(userId).pipe(
      map((user) => ({
        user,
        roles: {},
        policies: {},
      })),
    );
  }
}
